import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { DeckFileResponse, SessionDeck } from '@lp/shared';
import type { PDFDocumentProxy } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { api } from './api.js';

/**
 * A session's deck is either a PDF the browser renders itself with pdf.js, or
 * an HTML presentation run in a sandboxed frame on the sandbox origin. pdf.js
 * is loaded only when a PDF deck is actually opened, so a session without
 * one — and every other page — never pays for it.
 *
 * The legacy build is used on purpose: it carries the polyfills the modern
 * build leaves out, and a deck that will not open on a slightly older Safari is
 * worse than a few hundred extra kilobytes on the one tab that needs them.
 */

let pdfjs: Promise<typeof import('pdfjs-dist/legacy/build/pdf.mjs')> | null = null;

function loadPdfjs(): Promise<typeof import('pdfjs-dist/legacy/build/pdf.mjs')> {
  // The worker is bundled by Vite as an ordinary .js asset (a bare .mjs URL
  // would depend on the web server knowing that extension's MIME type), and
  // one worker is shared by every deck opened in the tab.
  pdfjs ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]).then(([lib, worker]) => {
    lib.GlobalWorkerOptions.workerPort = new worker.default();
    return lib;
  });
  return pdfjs;
}

type DeckFile = 'deck' | 'source';

function deckFileUrl(sessionId: string, deckId: string, kind: DeckFile, download = false): string {
  const q = new URLSearchParams({ kind, ...(download ? { download: 'true' } : {}) });
  return `/sessions/${encodeURIComponent(sessionId)}/decks/${encodeURIComponent(deckId)}/file?${q.toString()}`;
}

async function openDeckPdf(
  sessionId: string,
  deckId: string,
  signal: AbortSignal,
): Promise<PDFDocumentProxy> {
  const [{ url }, lib] = await Promise.all([
    api.get<DeckFileResponse>(deckFileUrl(sessionId, deckId, 'deck'), signal),
    loadPdfjs(),
  ]);
  // Fetched whole rather than in ranges: a deck is a few megabytes, and the
  // storage host does not expose the headers range loading relies on.
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error('The deck could not be downloaded.');
  const data = new Uint8Array(await res.arrayBuffer());
  // No WebAssembly: the app's CSP does not allow compiling it, and pdf.js
  // decodes images in plain JavaScript without it.
  return lib.getDocument({ data, useWasm: false }).promise;
}

/**
 * The open PDF for a session's deck. Keyed on `updatedAt` so a replaced deck is
 * fetched again rather than served from the cache.
 */
export function useDeckPdf(sessionId: string, deck: SessionDeck): UseQueryResult<PDFDocumentProxy> {
  return useQuery({
    queryKey: ['deck-pdf', sessionId, deck.id, deck.updatedAt],
    queryFn: ({ signal }) => openDeckPdf(sessionId, deck.id, signal),
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * The sandbox-origin address of an HTML deck, for its frame. The link lapses
 * within minutes, but a frame that has loaded keeps its deck, so it is fetched
 * once per mount — never refreshed underneath a reader mid-deck, which would
 * send them back to the first slide — and dropped as soon as nothing shows it.
 */
export function useDeckFrameUrl(sessionId: string, deck: SessionDeck): UseQueryResult<string> {
  return useQuery({
    queryKey: ['deck-frame', sessionId, deck.id, deck.updatedAt],
    queryFn: async ({ signal }) =>
      (await api.get<DeckFileResponse>(deckFileUrl(sessionId, deck.id, 'deck'), signal)).url,
    staleTime: Infinity,
    gcTime: 0,
    retry: 1,
  });
}

/** Mint a link at click time and follow it; the storage host forces a download. */
export async function downloadDeck(
  sessionId: string,
  deckId: string,
  kind: DeckFile,
): Promise<void> {
  const { url } = await api.get<DeckFileResponse>(deckFileUrl(sessionId, deckId, kind, true));
  window.location.assign(url);
}
